import React, { FC, useEffect, useMemo, useState } from 'react';
import { Typography } from '@rubin-dev/goblin';
import cx from 'classnames';
import { format } from '@aml/amount';
import { Network } from '@apolloGenerated';
import styles from './styles.module.scss';
import { useCurrencyListStore } from '@graph/libs';
import { BigNumber } from 'bignumber.js';
import { NetworkIconEnum } from '@shared/ui';

export type LineAmountProps = {
  amount: Record<string, number | string>;
  active?: boolean;
  network: Network;
  color?: string;
  token?: string;
};
export const LineAmount: FC<LineAmountProps> = ({
  amount,
  active,
  color,
  token,
}): JSX.Element => {
  const [isChanged, setIsChanged] = useState(false);

  const { currencies, graphCurrentCurrency } = useCurrencyListStore();

  const formattedAmount = useMemo(() => {
    const keys = Object.keys(amount);

    if (graphCurrentCurrency === NetworkIconEnum.Usd) {
      if (keys.length > 1) {
        return keys
          .reduce(
            (acc: number, currentValue) =>
              acc +
              parseFloat(
                String(
                  BigNumber(amount[currentValue])
                    .multipliedBy(currencies[currentValue.toLowerCase()])
                    .toFixed(2),
                ),
              ),
            0,
          )
          .toFixed(2);
      }

      if (!token) {
        return parseFloat(String(amount[keys[0]])).toFixed(2);
      }

      return format(
        parseFloat(
          String(
            BigNumber(amount[keys[0]])
              .multipliedBy(currencies[token?.toLowerCase()])
              .toFixed(2),
          ),
        ),
      );
    } else {
      if (keys.length > 1) {
        return keys
          .filter((key) => graphCurrentCurrency === key)
          .reduce((acc: number) => {
            return acc + parseFloat(String(amount[graphCurrentCurrency]));
          }, 0)
          .toFixed(2);
      }
    }

    return format(parseFloat(String(amount[keys[0]])).toFixed(2), 2) || '0';
  }, [currencies, amount, graphCurrentCurrency, token]);

  useEffect(() => {
    setIsChanged(true);
    setTimeout(() => setIsChanged(false), 1000);
  }, [amount]);

  return (
    <div
      className={cx(
        styles.lineAmount,
        active && styles.lineAmount_active,
        isChanged && styles.lineAmount_change,
      )}
    >
      <div className={styles.lineAmount__content}>
        <Typography
          className={styles.lineAmount__text}
          variant="body-16"
          style={{ color }}
        >
          {formattedAmount}
        </Typography>
      </div>
    </div>
  );
};
